import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Draggable } from '@fullcalendar/interaction';
import { Box, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import useSaveView from '../../hooks/useSaveView';
import taskProdGanttService from '../../service/taskProdGantt.service';
import ChipContent from './ChipContent';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const fitBounds = (value) => {
  const width = Math.min(Math.max(Number(value?.width) || 380, 280), window.innerWidth);
  const height = Math.min(Math.max(Number(value?.height) || 460, 180), window.innerHeight);
  return {
    width, height,
    x: Math.max(0, Math.min(Number(value?.x ?? window.innerWidth - width - 24) || 0, window.innerWidth - width)),
    y: Math.max(0, Math.min(Number(value?.y ?? 140) || 0, window.innerHeight - height)),
  };
};

const resizeHandles = [
  { direction: 'n', sx: { top: 0, left: 10, right: 10, height: 6, cursor: 'ns-resize' } },
  { direction: 's', sx: { bottom: 0, left: 10, right: 10, height: 6, cursor: 'ns-resize' } },
  { direction: 'w', sx: { left: 0, top: 10, bottom: 10, width: 6, cursor: 'ew-resize' } },
  { direction: 'e', sx: { right: 0, top: 10, bottom: 10, width: 6, cursor: 'ew-resize' } },
  { direction: 'nw', sx: { top: 0, left: 0, width: 10, height: 10, cursor: 'nwse-resize' } },
  { direction: 'ne', sx: { top: 0, right: 0, width: 10, height: 10, cursor: 'nesw-resize' } },
  { direction: 'sw', sx: { bottom: 0, left: 0, width: 10, height: 10, cursor: 'nesw-resize' } },
  { direction: 'se', sx: { bottom: 0, right: 0, width: 10, height: 10, cursor: 'nwse-resize' } },
];

const FloatingProductionStore = ({ user, canEdit, filters, activeResource, revision, onClose, onOpenProduction, getColorStyles, params }) => {
  const saveView = useSaveView(user);
  const [bounds, setBounds] = useState(() => fitBounds(user?.views?.productionFloatingStore));
  const boundsRef = useRef(bounds);
  const gesture = useRef(null);
  const listRef = useRef(null);
  const cardDragged = useRef(false);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let active = true;
    if (!filters.resourceOneId) {
      setOperations([]);
      setLoading(false);
      setFailed(false);
      return () => { active = false; };
    }
    setLoading(true);
    setFailed(false);
    taskProdGanttService.getUnstartedOperations(filters)
      .then((data) => { if (active) setOperations(data); })
      .catch(() => { if (active) { setOperations([]); setFailed(true); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters, revision, reload]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    const resize = () => {
      const next = fitBounds(boundsRef.current);
      boundsRef.current = next;
      setBounds(next);
    };
    window.addEventListener('resize', resize);
    return () => { clearInterval(timer); window.removeEventListener('resize', resize); };
  }, []);

  useEffect(() => {
    if (!canEdit || loading || !listRef.current) return;
    const draggable = new Draggable(listRef.current, {
      itemSelector: '.production-store-chip',
      appendTo: document.body,
      eventData: (element) => {
        cardDragged.current = true;
        const operation = operations.find((item) => item.id === Number(element.dataset.operationId));
        const duration = dayjs(operation.endDate).diff(dayjs(operation.startDate), 'second');
        const minutes = Number(operation.plannedQty) * (operation.quantityUnit?.code === params.QUANTITY_UNIT_HOUR_CODE ? 60 : 1);
        return {
          title: operation.operation?.operationName,
          duration: { seconds: duration > 0 ? duration : Math.max(60, minutes * 60 || 60) },
          backgroundColor: '#dbeafe',
          textColor: '#014AB1',
          extendedProps: {
            fromProductionStore: true,
            operationId: operation.id,
            eventData: { ...operation.production, type: 'PRODUCTION', productionOperation: [operation] },
          },
        };
      },
    });
    return () => draggable.destroy();
  }, [operations, canEdit, loading, params.QUANTITY_UNIT_HOUR_CODE]);

  const beginGesture = (event, type) => {
    if (event.button !== 0 || event.target.closest('[data-store-actions]')) return;
    event.stopPropagation();
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    gesture.current = { type, clientX: event.clientX, clientY: event.clientY, bounds: boundsRef.current };
  };

  const moveGesture = (event) => {
    if (!gesture.current) return;
    const { type, clientX, clientY, bounds: initial } = gesture.current;
    const dx = event.clientX - clientX;
    const dy = event.clientY - clientY;
    let next;
    if (type === 'move') {
      next = fitBounds({ ...initial, x: initial.x + dx, y: initial.y + dy });
    } else {
      let left = initial.x;
      let top = initial.y;
      let right = initial.x + initial.width;
      let bottom = initial.y + initial.height;
      const minWidth = Math.min(280, window.innerWidth);
      const minHeight = Math.min(180, window.innerHeight);
      if (type.includes('w')) left = Math.max(0, Math.min(initial.x + dx, right - minWidth));
      if (type.includes('e')) right = Math.min(window.innerWidth, Math.max(right + dx, left + minWidth));
      if (type.includes('n')) top = Math.max(0, Math.min(initial.y + dy, bottom - minHeight));
      if (type.includes('s')) bottom = Math.min(window.innerHeight, Math.max(bottom + dy, top + minHeight));
      next = { x: left, y: top, width: right - left, height: bottom - top };
    }
    boundsRef.current = next;
    setBounds(next);
  };

  const endGesture = async () => {
    if (!gesture.current) return;
    const initial = gesture.current.bounds;
    gesture.current = null;
    const current = boundsRef.current;
    if (initial.x === current.x && initial.y === current.y &&
        initial.width === current.width && initial.height === current.height) return;
    if ((await saveView('productionFloatingStore', current)) === false) {
      addNotification({ content: 'A gyártástár ablakállapotának mentése sikertelen.', type: NotificationType.ERROR });
    }
  };

  const handleProps = (type) => ({
    onPointerDown: (event) => beginGesture(event, type),
    onPointerMove: moveGesture,
    onPointerUp: endGesture,
    onPointerCancel: endGesture,
    onLostPointerCapture: endGesture,
    sx: { cursor: type === 'move' ? 'move' : undefined, touchAction: 'none', userSelect: 'none' },
  });

  return createPortal(
    <Paper role="region" aria-label="Gyártástár" elevation={8} sx={{
      position: 'fixed', left: bounds.x, top: bounds.y, width: bounds.width, height: bounds.height,
      zIndex: 1200, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid', borderColor: 'divider',
    }}>
      <Box display="flex" alignItems="center" gap={0.5} px={1} py={0.5} bgcolor="action.hover" {...handleProps('move')}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight="bold">Gyártástár ({operations.length})</Typography>
          <Typography variant="caption" noWrap display="block" title={activeResource?.title}>
            {activeResource?.title || 'Nincs kiválasztott erőforrás'}
          </Typography>
        </Box>
        <Box data-store-actions display="flex" alignItems="center" sx={{ cursor: 'default' }}>
          <Tooltip title="Frissítés"><span><IconButton aria-label="Frissítés" disabled={loading} onClick={() => setReload((value) => value + 1)}><RefreshIcon /></IconButton></span></Tooltip>
          <IconButton aria-label="Gyártástár bezárása" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </Box>
      <Typography variant="caption" px={2} py={1}>
        {!activeResource ? 'Válasszon erőforrást a naptár erőforráslistájában.' : canEdit ? 'Húzza a műveletet egy gép kívánt időpontjára.' : 'A tervezéshez szerkesztési jogosultság szükséges.'}
      </Typography>
      <Box ref={listRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1 }}>
        {!activeResource ? null : loading ? <Typography p={1}>Betöltés…</Typography> : failed ? <Typography p={1} color="error">A gyártástár betöltése sikertelen.</Typography> : operations.length === 0 ? <Typography p={1}>Nincs meg nem kezdett művelet.</Typography> : operations.map((operation) => {
          const overdue = dayjs(operation.startDate).valueOf() < now;
          const eventData = { ...operation.production, type: 'PRODUCTION', productionOperation: [operation] };
          const colors = getColorStyles({ ...operation.production, ...operation });
          return (
            <Box key={operation.id} data-operation-id={operation.id} className="production-store-chip"
              role="button" tabIndex={0}
              onPointerDownCapture={() => { cardDragged.current = false; }}
              onClick={() => { if (!cardDragged.current) onOpenProduction(operation.productionId); }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpenProduction(operation.productionId);
                }
              }}
              title={`${operation.production.workingNumber} – ${operation.operation?.operationName || ''}`}
              sx={{ mb: 1, p: 1, borderRadius: 1, border: '1px solid', borderColor: overdue ? '#dc2626' : 'divider',
                bgcolor: overdue ? '#fee2e2' : colors.backgroundColor, color: overdue ? '#b91c1c' : colors.textColor,
                cursor: canEdit ? 'grab' : 'pointer', fontSize: 13, fontWeight: 'bold', overflow: 'hidden' }}>
              {params.TASKGANTT_PRODUCTION_CHIP_CONTENT
                ? <ChipContent eventInfo={{ event: { _def: { extendedProps: { eventData } } } }} />
                : <div>{operation.production.workingNumber} – {operation.operation?.operationName}</div>}
              <Typography variant="caption" display="block">
                {overdue ? 'Elmaradt kezdés: ' : 'Tervezett kezdés: '}{dayjs(operation.startDate).format('YYYY.MM.DD. HH:mm')}
              </Typography>
            </Box>
          );
        })}
      </Box>
      {resizeHandles.map(({ direction, sx }) => (
        <Box key={direction} aria-hidden="true" {...handleProps(direction)}
          sx={{ position: 'absolute', zIndex: 1, touchAction: 'none', userSelect: 'none', ...sx }} />
      ))}
    </Paper>, document.body
  );
};

export default FloatingProductionStore;
